import { useIonRouter } from '@ionic/react';
import {
  ArrowsClockwiseIcon,
  BugIcon,
  EyeIcon,
  FirstAidIcon,
  GlobeIcon,
  IconProps,
  PhoneOutgoingIcon,
  UserIcon,
  YoutubeLogoIcon,
} from '@phosphor-icons/react';
import React, { useCallback, useMemo } from 'react';

import SyncPets from '../../components/Pet/SyncPets';
import FormHeader from '../../components/UI/FormHeader';
import { MenuItem } from '../../components/UI/UserMenuSection/types';
import DiagnosticsPanel from '../../components/User/DiagnosticsPanel';
import ProblemHelp from '../../components/User/ProblemHelp';
import UserForm from '../../components/User/UserForm';
import { usePets } from '../../context/PetsContext';
import { useTutorial } from '../../context/TutorialContext';
import { useUser } from '../../context/UserContext';
import { transformUser } from '../../types/User/types';
import { openInAppBrowser } from '../../utils/capacitorBrowser';
import {
  COVERAGE_EXPIRED_ALERT,
  hasExpiredCoverage,
  isDawerPet,
} from '../../utils/coverage';
import { getDiagnosticsAccessLevel } from '../../utils/diagnosticsAccess';
import { buildTrackingUserKey } from '../../utils/tracking';
import { useAuthActions } from '../Auth/useAuth';
import {
  DAWER_PHONE_CONSULTATION_UNAVAILABLE_ALERT,
  isDawerPhoneConsultationOpen,
  usePhoneConsultation,
} from '../Otros';
import { useAlert } from '../UI/useAlert';
import { useModal } from '../UI/useModal';
import { useAppVersion } from '../Utils/useAppVersion';

const ICON_SIZE = '22';
const iconProps: IconProps = { size: ICON_SIZE, weight: 'bold' };

export const useUserPage = () => {
  const { push } = useIonRouter();
  const { logout } = useAuthActions();

  const { pets, currentPet, datosExtra } = usePets();
  const { user } = useUser();
  const { setTutorialDone, setStartTour } = useTutorial();
  const { isOpen, content, openModal, closeModal, modalProps } = useModal();
  const { alertState, showAlert, hideAlert } = useAlert();
  const appVersion = useAppVersion();
  const coverageExpired = hasExpiredCoverage(currentPet);
  const isCurrentPetDawer = isDawerPet(currentPet);

  const { userData, addressData } = transformUser(user);
  const isLoaded = Boolean(user);
  const name = user ? `${user.nombre ?? ''} ${user.apellido ?? ''}`.trim() : '';
  const email = user?.email ?? '';
  const trackingUserKey = buildTrackingUserKey(email);
  const normalizedEmail = email.trim().toLowerCase();

  const diagnosticsAccessLevel = useMemo(
    () => getDiagnosticsAccessLevel(normalizedEmail),
    [normalizedEmail],
  );
  const canSeeDiagnosticsPanel = diagnosticsAccessLevel !== 'none';

  const goBack = useCallback(() => push('/tabs/home'), [push]);

  const openCoverageRequiredAlert = useCallback(() => {
    showAlert({
      ...COVERAGE_EXPIRED_ALERT,
      buttons: [{ text: 'Entendido' }],
    });
  }, [showAlert]);

  const openUserForm = useCallback(() => {
    openModal(
      <UserForm closeModal={closeModal} userData={userData} addressData={addressData} />,
      { navigation: false },
    );
  }, [openModal, closeModal, userData, addressData]);

  const openSyncPets = useCallback(() => {
    openModal(
      <SyncPets
        header={
          <FormHeader
            icon={<ArrowsClockwiseIcon weight="fill" />}
            title="Sincronizar"
            subtitle="Traé tus mascotas a la app"
          />
        }
      />,
    );
  }, [openModal]);

  const openTutorial = useCallback(() => {
    setTutorialDone(false);
    setStartTour(true);
    push('/tabs/home');
  }, [setStartTour, setTutorialDone, push]);

  const openWebVersion = useCallback(async () => {
    await openInAppBrowser('https://app.fielpet.com');
  }, []);

  const youtubeLink = datosExtra?.youtube?.link;
  const phoneConsultationLink = isCurrentPetDawer
    ? datosExtra?.telefonia?.celTigre ?? datosExtra?.telefonia?.link
    : datosExtra?.telefonia?.link;

  const openYoutube = useCallback(async () => {
    if (youtubeLink) {
      await openInAppBrowser(youtubeLink);
    }
  }, [youtubeLink]);

  const { openPhoneConsultation, phoneNumber } = usePhoneConsultation({
    phoneLink: phoneConsultationLink,
    showAlert,
    canOpen: isCurrentPetDawer ? isDawerPhoneConsultationOpen : undefined,
    unavailableAlert: DAWER_PHONE_CONSULTATION_UNAVAILABLE_ALERT,
  });

  const openProblemHelp = useCallback(() => {
    openModal(<ProblemHelp closeModal={closeModal} userKey={trackingUserKey} />, {
      navigation: false,
    });
  }, [openModal, closeModal, trackingUserKey]);

  const openDiagnosticsPanel = useCallback(() => {
    if (!canSeeDiagnosticsPanel) {
      showAlert({
        header: 'Acceso restringido',
        message: 'Este panel está disponible solo para desarrolladores.',
        buttons: [{ text: 'Entendido' }],
      });
      return;
    }

    openModal(
      <DiagnosticsPanel
        closeModal={closeModal}
        userKey={trackingUserKey}
        accessLevel={diagnosticsAccessLevel}
      />,
      {
        navigation: false,
      },
    );
  }, [
    openModal,
    closeModal,
    trackingUserKey,
    diagnosticsAccessLevel,
    canSeeDiagnosticsPanel,
    showAlert,
  ]);

  const menuItems: MenuItem[] = useMemo(
    () => [
      {
        label: user ? 'Editar mis datos' : 'Completar mis datos',
        icon: <UserIcon {...iconProps} />,
        onClick: openUserForm,
        type: 'cuenta' as const,
      },
      {
        label: 'Sincronizar mascotas',
        icon: <ArrowsClockwiseIcon {...iconProps} />,
        onClick: coverageExpired ? openCoverageRequiredAlert : openSyncPets,
        hidden: isCurrentPetDawer,
        type: 'cuenta' as const,
      },
      {
        label: 'Ver tutorial de la app',
        icon: <EyeIcon {...iconProps} />,
        onClick: openTutorial,
        hidden: !pets?.length,
        type: 'soporte' as const,
      },
      {
        label: 'Como generar un reintegro',
        icon: <YoutubeLogoIcon {...iconProps} />,
        onClick: coverageExpired ? openCoverageRequiredAlert : openYoutube,
        hidden: !pets?.length || !youtubeLink,
        type: 'soporte' as const,
      },
      {
        label: 'Reportar un problema',
        icon: <FirstAidIcon {...iconProps} />,
        onClick: coverageExpired ? openCoverageRequiredAlert : openProblemHelp,
        type: 'soporte' as const,
      },
      {
        label: 'Panel del desarrollador',
        icon: <BugIcon {...iconProps} />,
        onClick: coverageExpired ? openCoverageRequiredAlert : openDiagnosticsPanel,
        hidden: !canSeeDiagnosticsPanel,
        type: 'soporte' as const,
      },
      {
        label: 'Consulta telefónica',
        icon: <PhoneOutgoingIcon {...iconProps} />,
        onClick: coverageExpired ? openCoverageRequiredAlert : openPhoneConsultation,
        hidden: !pets?.length,
        type: 'soporte' as const,
      },
      {
        label: 'Versión compatible',
        icon: <GlobeIcon {...iconProps} />,
        onClick: openWebVersion,
        type: 'soporte' as const,
      },
    ],
    [
      user,
      pets,
      isCurrentPetDawer,
      coverageExpired,
      openUserForm,
      openSyncPets,
      openCoverageRequiredAlert,
      openTutorial,
      youtubeLink,
      openYoutube,
      openProblemHelp,
      openDiagnosticsPanel,
      canSeeDiagnosticsPanel,
      openPhoneConsultation,
      openWebVersion,
    ],
  );

  const cuentaItems = useMemo(
    () => menuItems.filter((item) => item.type === 'cuenta'),
    [menuItems],
  );

  const soporteItems = useMemo(
    () => menuItems.filter((item) => item.type === 'soporte'),
    [menuItems],
  );

  const showLogoutAlert = () => {
    showAlert({
      header: 'Cerrar sesión',
      message: '¿Deseas cerrar sesión?',
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        {
          text: 'Confirmar',
          handler: async () => {
            await logout();
          },
        },
      ],
    });
  };

  return {
    userInfo: {
      isLoaded,
      name,
      email,
    },
    appVersion,
    menu: {
      cuentaItems,
      soporteItems,
    },
    modal: {
      isOpen,
      content,
      closeModal,
      ...modalProps,
    },
    alert: {
      ...alertState,
      hideAlert,
    },
    goBack,
    showLogoutAlert,
  };
};
